import React, { useState } from 'react';
import { api } from './api/api';

// ADMIN-only: Discord channels fed by fobase webhooks (backend /api/v1/discord/**).
// Automatic messages normally go out from the nightly race-analysis job (Wed/Sat 00:00 UTC);
// here they can be re-sent at any time, plus a free-form message as either bot.

const CHANNELS = [
  { value: 'RACE_RESULTS', label: 'Race results channel' },
  { value: 'WEATHER', label: 'Similar weather channel' },
];
const MAX_LEN = 4000;

const errText = (e) =>
  (typeof e.response?.data === 'string' && e.response.data) || e.response?.data?.message || e.message || 'Error';

const btn = (primary) => ({
  padding: '7px 16px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 13, fontWeight: 600,
  background: primary ? 'var(--c-accent)' : '#f9fafb',
  border: primary ? 'none' : '1px solid var(--c-border)',
  color: primary ? '#fff' : 'var(--c-text)',
});

const Status = ({ s }) => {
  if (!s) return null;
  if (s.loading) return <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>Sending…</span>;
  return <span style={{ fontSize: 12, color: s.err ? 'var(--c-error)' : '#16a34a' }}>{s.err || s.msg}</span>;
};

const DiscordModal = ({ onClose }) => {
  const [status, setStatus] = useState({});
  const [channel, setChannel] = useState('RACE_RESULTS');
  const [text, setText] = useState('');

  const run = async (key, request) => {
    setStatus(s => ({ ...s, [key]: { loading: true } }));
    try {
      const res = await request();
      setStatus(s => ({ ...s, [key]: { msg: String(res.data || 'Sent') } }));
      return true;
    } catch (e) {
      setStatus(s => ({ ...s, [key]: { err: errText(e) } }));
      return false;
    }
  };

  const sendCustom = async () => {
    const ok = await run('custom', () => api.post('/discord/message', { channel, text: text.trim() }));
    if (ok) setText('');
  };

  const busy = Object.values(status).some(s => s?.loading);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 'var(--radius-lg)', padding: 24, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Discord</div>
          <button onClick={onClose} style={btn(false)}>Close</button>
        </div>

        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Send now</div>
        <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginBottom: 10 }}>
          Same messages the nightly job posts after each race. Race results use ~2 GPRO API requests per team manager.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button disabled={busy} style={btn(true)}
              onClick={() => { if (window.confirm('Post race results of the last race to the race results channel?')) run('results', () => api.post('/discord/race-results')); }}>
              🏁 Send race results
            </button>
            <Status s={status.results} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button disabled={busy} style={btn(true)}
              onClick={() => { if (window.confirm('Post similar weather for the next race to the weather channel?')) run('weather', () => api.post('/discord/weather')); }}>
              🌦️ Send similar weather
            </button>
            <Status s={status.weather} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button disabled={busy} style={btn(true)}
              onClick={() => { if (window.confirm('Post driver/TD signings of the latest market (Elite + Master) to the signings channel?')) run('market', () => api.post('/discord/market-transfers')); }}>
              ✍️ Send latest market signings
            </button>
            <Status s={status.market} />
          </div>
        </div>

        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Custom message</div>
        <select value={channel} onChange={e => setChannel(e.target.value)}
          style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--c-border)', borderRadius: 'var(--radius-sm)', fontSize: 13, background: '#fff', marginBottom: 8 }}>
          {CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <textarea value={text} onChange={e => setText(e.target.value)} maxLength={MAX_LEN} rows={7}
          placeholder="Message text (Discord markdown: **bold**, *italic*, > quote …)"
          style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--c-border)', borderRadius: 'var(--radius-sm)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
            {text.length}/{MAX_LEN} · longer than 1900 chars is split into several messages · @mentions are disabled
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Status s={status.custom} />
            <button disabled={busy || !text.trim()} style={btn(true)} onClick={sendCustom}>Send</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscordModal;
